import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { color as defaultColors } from 'theme';

/**
 * Custom hook to get team-specific colors with fallbacks to default theme
 * Used for theming the app based on team configuration (e.g., knickstime.com uses Knicks colors)
 *
 * @returns {Object} Team colors with fallbacks
 * @property {string} mainColor - Primary team color (falls back to default red)
 * @property {string} altColor - Secondary/alternate team color (falls back to default red)
 * @property {boolean} isTeamSite - Whether this is a team-specific site
 */
export const useTeamColors = () => {
  const configuration = useSelector((state) => state.configuration?.data);

  const teamColors = useMemo(() => {
    // Ensure colors have # prefix
    const ensureHashPrefix = (color) => {
      if (!color) return null;
      return color.startsWith('#') ? color : `#${color}`;
    };

    const mainColor =
      ensureHashPrefix(configuration?.style_main_color) || defaultColors.red;
    const altColor =
      ensureHashPrefix(configuration?.style_alternate_color) ||
      defaultColors.white;
    const isTeamSite = Boolean(configuration?.style_main_color);

    return {
      mainColor,
      altColor,
      isTeamSite,
      // Helper to get rgba version with opacity
      mainColorRgba: (opacity = 0.1) => hexToRgba(mainColor, opacity),
      altColorRgba: (opacity = 0.1) => hexToRgba(altColor, opacity),
    };
  }, [configuration?.style_main_color, configuration?.style_alternate_color]);

  return teamColors;
};

/**
 * Convert hex color to rgba with specified opacity
 * @param {string} hex - Hex color code
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} rgba color string
 */
const hexToRgba = (hex, opacity = 1) => {
  if (!hex) return `rgba(255, 51, 102, ${opacity})`; // Default red

  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
